import React, { useState, useContext, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  VehicleDetails,
  PricesPriorityOrder,
  UsedPricesPriorityOrder,
  NEGATIVE_PRICE_KEYS,
  CanadianPricesPriorityOrder,
  TAXES_AND_LEVIES_KEYS,
} from '../../types';
import { vehicleContext } from '../VehicleProvider';
import Modal from '../Modal';
import { COLORS } from '../../styles/variables';
import {
  Container,
  InnerContainer,
  Legal,
  DisclaimerTitleWrapper,
  TextContainer,
} from './PriceBreakdown-T3.styles';
import { HeadlineOrderTwo } from '../Common/Common.style';
import { FilteredPriceBreakdownConfig } from '../../redux/priceBreakdownReducer';
import { formatPrice } from '../../utils/currencyTools';
import {
  Tier3Wrapper,
  SectionTitle,
  Container as FAContainer,
} from '../ContentWrapper/ContentWrapper.styles';
import { shouldShowFinalPrice, getVisiblePricesCount } from '../../utils/priceUtils';
import BreakdownRow from './PriceRow';

export interface BreakdownProps {
  breakdownConfig: FilteredPriceBreakdownConfig;
  displayRebates: boolean;
  displayRebatesUsed: boolean;
}

const PriceBreakdownInfoBoxT3: React.FC<BreakdownProps> = ({
  breakdownConfig,
  displayRebates,
  displayRebatesUsed,
}) => {
  const { t } = useTranslation();

  const { vehicle, market } = useContext(vehicleContext);
  const { typedPrices, rebate } = (vehicle as VehicleDetails) ?? {};
  const isUsedVehicle = market.includes('uc');
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    text: string | JSX.Element;
  }>({
    isOpen: false,
    text: '',
  });

  const onModalOpen = useCallback((e: any, text?: string | JSX.Element) => {
    e.preventDefault();
    e.stopPropagation();
    setModalState({
      isOpen: true,
      text: text || '',
    });
  }, []);

  const onModalClose = useCallback(() => {
    setModalState({
      isOpen: false,
      text: '',
    });
  }, []);

  const isCanadian = useMemo(() => {
    return market === 'ca' || market === 'cauc';
  }, [market]);

  const showRebates = useMemo(() => {
    const displayRebatesConfig = isUsedVehicle ? displayRebatesUsed : displayRebates;
    if (!displayRebatesConfig || !rebate?.length) return false;
    return true;
  }, [displayRebates, displayRebatesUsed, isUsedVehicle, rebate?.length]);

  const showFinalPrice = useMemo(() => {
    if (!breakdownConfig) return false;
    const rebateLength = rebate?.length ?? 0;
    return shouldShowFinalPrice(
      breakdownConfig,
      isUsedVehicle,
      displayRebates,
      rebateLength,
      typedPrices,
    );
  }, [breakdownConfig, displayRebates, isUsedVehicle, rebate?.length, typedPrices]);

  const renderMultiplePrices = useMemo(() => {
    if (!breakdownConfig) return null;
    const vehicleType = isUsedVehicle ? 'used' : 'new';
    // sort price options for used vehicles are different from new vehicles
    // US Prices Sequence
    const usPricesPriority = isUsedVehicle ? UsedPricesPriorityOrder : PricesPriorityOrder;
    // Canadian Prices Sequence based on ticket narshop-1390(comments) - for both New and Used Vehicles
    const caPricesPriority = CanadianPricesPriorityOrder;
    // Choose between US or CA based on the market type
    const pricesPriority = isCanadian ? caPricesPriority : usPricesPriority;
    const sortedBreakdown = Object.entries(breakdownConfig)
      .filter(([key, value]) => {
        const { priceKey } = value;
        // Filter out Final Price because we show final price at the end of price rows after rebates
        if (!value.isEnabled || value.vehicleType !== vehicleType || priceKey === 'final')
          return false;
        const priceAmount = typedPrices.find((element) => element?.type === priceKey);
        const isOverrideEnabled = value?.hasTextOverride;
        const hasNoAmount = !priceAmount || !priceAmount?.amount;
        // if there is no data for this price field and text override is not enabled in CFM then hide this price row
        if (hasNoAmount && !isOverrideEnabled) return false;
        // TEMPORARILY LOGIC for NARSHOP-1164
        // if absolute value of dealer discount is equal to regular price (msrp) then hide dealer discount row
        const regularPrice = typedPrices.find((element) => element?.type === 'regular');
        const regularPriceAmount = regularPrice?.amount;
        if (
          priceKey === 'dealerDiscount' &&
          priceAmount?.amount &&
          regularPriceAmount === Math.abs(priceAmount.amount) &&
          priceAmount?.amount < 0
        ) {
          return null;
        }
        return true;
      })
      .map(([key, value]) => ({ key, value }))
      .sort((a, b) => {
        const priorityA = pricesPriority.indexOf(a.value.priceKey);
        const priorityB = pricesPriority.indexOf(b.value.priceKey);
        return priorityA - priorityB;
      });
    if (!sortedBreakdown?.length) return null;

    return sortedBreakdown?.map((item, index) => {
      const priceConfig = item.value;
      const { priceKey } = priceConfig;
      const priceAmount = typedPrices.find((element) => element?.type === priceKey);
      const hasNoAmount = !priceAmount || !priceAmount?.amount;

      const textOverrideCopy =
        priceConfig?.textOverride || t(t(`pricingBreakdown.priceDetails.textOverrideDefault`));
      const shouldBeNegative = NEGATIVE_PRICE_KEYS.includes(priceKey);
      // First Price should be bold if it's price key is either regular or retail
      const isBold = index === 0 && (priceKey === 'regular' || priceKey === 'retail');

      return (
        <BreakdownRow
          key={priceKey}
          priceKey={priceKey}
          // Only For Canadian New Vehicles First Price
          noBottomBorder={isCanadian && !isUsedVehicle && index === 0}
          labelVariant={isBold ? 'h4' : ''}
          hasIndent={TAXES_AND_LEVIES_KEYS.includes(priceKey)}
          label={priceConfig.label || ''}
          isMain={isBold}
          longDisclaimer={priceConfig?.longDisclaimer || ''}
          shortDisclaimer={priceConfig?.shortDisclaimer || ''}
          onModalOpen={onModalOpen}
          priceValue={
            hasNoAmount ? textOverrideCopy : formatPrice(priceAmount?.amount, shouldBeNegative)
          }
        />
      );
    });
  }, [breakdownConfig, isCanadian, isUsedVehicle, onModalOpen, t, typedPrices]);

  const renderRebates = useMemo(() => {
    if (!showRebates) return null;
    return rebate?.map((item, index) => {
      const priceDetail = item.Value;
      const priceKey = `rebate-${index}`;
      // If for a rebate, offerName or price value is empty do not show that rebate at all
      if (!priceDetail || !item?.Name) return null;

      return (
        <BreakdownRow
          key={priceKey}
          priceKey={priceKey}
          label={item?.Name || ''}
          isMain={false}
          longDisclaimer={item?.offerDescription || ''}
          shortDisclaimer=""
          onModalOpen={onModalOpen}
          priceValue={priceDetail ? formatPrice(priceDetail, true) : '—'}
        />
      );
    });
  }, [onModalOpen, rebate, showRebates]);

  const renderFinalPrice = useMemo(() => {
    if (!breakdownConfig) return null;

    const finalPriceConfig = breakdownConfig?.final;
    if (!finalPriceConfig?.isEnabled || !showFinalPrice) return null;

    const priceAmount = typedPrices.find((element) => element?.type === finalPriceConfig?.priceKey);
    if (!priceAmount?.amount && !finalPriceConfig?.hasTextOverride) return null;
    const isMainPrice = true;
    const textOverrideCopy =
      finalPriceConfig?.textOverride || t(t(`pricingBreakdown.priceDetails.textOverrideDefault`));

    return (
      <BreakdownRow
        key="final"
        labelVariant="h3"
        noBottomBorder
        priceKey="final"
        label={finalPriceConfig.label || ''}
        isMain={isMainPrice}
        longDisclaimer={finalPriceConfig?.longDisclaimer || ''}
        shortDisclaimer={finalPriceConfig?.shortDisclaimer || ''}
        onModalOpen={onModalOpen}
        priceValue={priceAmount?.amount ? formatPrice(priceAmount.amount) : textOverrideCopy}
      />
    );
  }, [breakdownConfig, onModalOpen, showFinalPrice, t, typedPrices]);

  // Total count of prices that FA will show
  const visiblePricesCount = useMemo(() => {
    if (!breakdownConfig || !typedPrices) return 0;
    return getVisiblePricesCount(
      breakdownConfig,
      isUsedVehicle,
      displayRebates,
      displayRebatesUsed,
      rebate?.length ?? 0,
      typedPrices,
      showFinalPrice,
    );
  }, [
    breakdownConfig,
    displayRebates,
    displayRebatesUsed,
    isUsedVehicle,
    rebate?.length,
    showFinalPrice,
    typedPrices,
  ]);

  // If either only one price or no price is available to show to user then
  // the Pricing Breakdown FA should be hidden on the VDP.
  if (!vehicle || !breakdownConfig || visiblePricesCount < 2) return null;

  return (
    <Tier3Wrapper>
      <SectionTitle displayOnMobile>{t('pricingBreakdown.priceDetails.heading')}</SectionTitle>
      <FAContainer>
        <Container data-testid="info-box-container">
          <InnerContainer>
            {renderMultiplePrices}
            {renderRebates}
            {renderFinalPrice}
          </InnerContainer>
        </Container>
      </FAContainer>
      <Modal isOpen={modalState?.isOpen} onClose={onModalClose} data-cy="legal-disclaimer-modal">
        <Legal>
          <DisclaimerTitleWrapper>
            <HeadlineOrderTwo color={COLORS.black}>
              {t(`pricingBreakdown.priceDetails.legalDisclaimer`)}
            </HeadlineOrderTwo>
          </DisclaimerTitleWrapper>
          <TextContainer>{modalState?.text}</TextContainer>
        </Legal>
      </Modal>
    </Tier3Wrapper>
  );
};

export default PriceBreakdownInfoBoxT3;
